import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import { MapPin, Navigation, ExternalLink, Shield, X, Maximize2, ZoomIn } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Component to handle map resize/invalidation when container size changes
const MapResizer = () => {
  const map = useMap();
  
  useEffect(() => {
    // Invalidate size after a short delay to ensure container is fully rendered
    const timer = setTimeout(() => {
      map.invalidateSize();
    }, 100);
    
    // Also invalidate on window resize
    const handleResize = () => map.invalidateSize();
    window.addEventListener('resize', handleResize);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, [map]);
  
  return null;
};

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Island coordinates
const ISLAND_COORDINATES = {
  'aruba': { lat: 12.5211, lng: -69.9683, zoom: 12 },
  'bonaire': { lat: 12.2019, lng: -68.2624, zoom: 12 },
  'curacao': { lat: 12.1696, lng: -68.9900, zoom: 12 },
  'curaçao': { lat: 12.1696, lng: -68.9900, zoom: 12 },
};

// City coordinates within islands
const CITY_COORDINATES = {
  // Curaçao cities
  'willemstad': { lat: 12.1091, lng: -68.9316 },
  'punda': { lat: 12.1050, lng: -68.9350 },
  'otrobanda': { lat: 12.1080, lng: -68.9380 },
  'pietermaai': { lat: 12.1100, lng: -68.9280 },
  'jan thiel': { lat: 12.0850, lng: -68.8650 },
  'mambo beach': { lat: 12.0900, lng: -68.8700 },
  'santa rosa': { lat: 12.1300, lng: -68.9700 },
  'julianadorp': { lat: 12.1700, lng: -68.9900 },
  // Aruba cities
  'oranjestad': { lat: 12.5092, lng: -70.0086 },
  'san nicolas': { lat: 12.4372, lng: -69.9030 },
  'noord': { lat: 12.5600, lng: -70.0200 },
  'palm beach': { lat: 12.5750, lng: -70.0550 },
  'eagle beach': { lat: 12.5500, lng: -70.0500 },
  // Bonaire cities
  'kralendijk': { lat: 12.1443, lng: -68.2655 },
  'rincon': { lat: 12.2200, lng: -68.3050 },
};

const ListingMap = ({ location, title, compact = false, t = (key) => key }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Prevent body scroll when modal is open - must be before any early returns
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isModalOpen]);
  
  // Check if location has meaningful data (not just empty object or default values)
  const hasValidLocation = () => {
    if (!location) return false;
    
    const loc = typeof location === 'object' ? location : {};
    
    // Check for explicit coordinates
    if (loc.latitude && loc.longitude) return true;
    if (loc.coordinates?.lat && loc.coordinates?.lng) return true;
    
    // Check for city/area/address
    if (loc.city || loc.area || loc.address) return true;
    
    // Check for island/country (but only if it's not just a default)
    if (loc.island || loc.country) return true;
    
    return false;
  };
  
  // Get coordinates from location
  const getCoordinates = () => {
    if (!location) return null;
    
    const loc = typeof location === 'object' ? location : {};
    
    // First check if we have explicit coordinates
    if (loc.latitude && loc.longitude) {
      return { lat: loc.latitude, lng: loc.longitude };
    }
    if (loc.coordinates?.lat && loc.coordinates?.lng) {
      return loc.coordinates;
    }
    
    // Try to match city name
    const city = (loc.city || loc.area || '').toLowerCase().trim();
    if (CITY_COORDINATES[city]) {
      return CITY_COORDINATES[city];
    }
    
    // Try to match island/country
    const island = (loc.island || loc.country || '').toLowerCase().trim();
    for (const [key, coords] of Object.entries(ISLAND_COORDINATES)) {
      if (island.includes(key)) {
        return { lat: coords.lat, lng: coords.lng };
      }
    }
    
    // No valid coordinates found
    return null;
  };
  
  // Early return if no valid location - show placeholder
  if (!hasValidLocation()) {
    if (compact) {
      return (
        <div className="space-y-2" data-testid="listing-map-no-location">
          <div className="h-[140px] bg-secondary/50 rounded-xl flex flex-col items-center justify-center border border-border/50">
            <MapPin className="w-6 h-6 text-muted-foreground/50 mb-2" />
            <p className="text-sm text-muted-foreground text-center px-4">
              {t('location_on_request')}
            </p>
          </div>
        </div>
      );
    }
    return (
      <div className="h-64 bg-secondary/50 rounded-xl flex flex-col items-center justify-center border border-border/50" data-testid="listing-map-no-location">
        <MapPin className="w-8 h-8 text-muted-foreground/50 mb-3" />
        <p className="text-muted-foreground">{t('location_on_request')}</p>
      </div>
    );
  }
  
  const originalCoords = getCoordinates();
  
  // If still no coords after all attempts, show placeholder
  if (!originalCoords) {
    if (compact) {
      return (
        <div className="space-y-2" data-testid="listing-map-no-location">
          <div className="h-[140px] bg-secondary/50 rounded-xl flex flex-col items-center justify-center border border-border/50">
            <MapPin className="w-6 h-6 text-muted-foreground/50 mb-2" />
            <p className="text-sm text-muted-foreground text-center px-4">
              {t('location_on_request')}
            </p>
          </div>
        </div>
      );
    }
    return (
      <div className="h-64 bg-secondary/50 rounded-xl flex flex-col items-center justify-center border border-border/50" data-testid="listing-map-no-location">
        <MapPin className="w-8 h-8 text-muted-foreground/50 mb-3" />
        <p className="text-muted-foreground">{t('location_on_request')}</p>
      </div>
    );
  }
  
  const displayType = location?.display_type || 'exact';
  const isAreaMode = displayType === 'area';
  
  // For area mode: Generate a random but consistent offset based on coordinates
  // This ensures the displayed center is NOT the user's exact location
  // But it's always the same offset for this listing (seeded by coords)
  const getOffsetCoords = (coords) => {
    if (!isAreaMode || !coords) return coords;
    
    // Use coordinates as seed for consistent pseudo-random offset
    const seed = (coords.lat * 1000000 + coords.lng * 1000000) % 1000;
    
    // Generate pseudo-random angle (0-360 degrees) and distance (200-400 meters)
    const angle = ((seed * 137.5) % 360) * (Math.PI / 180); // Golden angle for good distribution
    const distance = 200 + (seed % 200); // 200-400 meters offset
    
    // Convert distance to lat/lng offset (rough approximation)
    // 1 degree latitude ≈ 111,000 meters
    // 1 degree longitude ≈ 111,000 * cos(latitude) meters
    const latOffset = (distance * Math.cos(angle)) / 111000;
    const lngOffset = (distance * Math.sin(angle)) / (111000 * Math.cos(coords.lat * Math.PI / 180));
    
    return {
      lat: coords.lat + latOffset,
      lng: coords.lng + lngOffset
    };
  };
  
  // Use offset coordinates for area mode display
  const coords = isAreaMode ? getOffsetCoords(originalCoords) : originalCoords;
  
  // Full address for exact mode
  const fullLocationText = typeof location === 'object' 
    ? [location.address, location.city, location.area, location.country].filter(Boolean).join(', ')
    : location;

  // For area mode, don't include the exact address in the display
  const displayLocationText = isAreaMode && typeof location === 'object'
    ? [location.city, location.area, location.country].filter(Boolean).join(', ')
    : fullLocationText;

  // Build area-level destination (no exact coordinates for privacy)
  const getAreaDestination = () => {
    const parts = [location.city, location.area, location.country].filter(Boolean);
    return encodeURIComponent(parts.join(', '));
  };

  // Open directions - uses Google Maps or native maps app
  // IMPORTANT: For area mode, we use city/area name instead of exact coordinates for privacy
  const openDirections = () => {
    let destination;
    
    if (isAreaMode) {
      // Privacy mode: Use general area name, NOT exact coordinates
      destination = getAreaDestination();
    } else {
      // Exact mode: Use precise coordinates
      destination = `${coords.lat},${coords.lng}`;
    }
    
    // Check if on mobile for native app support
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    if (isMobile) {
      // Try to open native maps app
      window.location.href = `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
    } else {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${destination}`, '_blank');
    }
  };

  // Open location in Google Maps
  // IMPORTANT: For area mode, we use city/area search, NOT exact coordinates
  const openInGoogleMaps = () => {
    if (isAreaMode) {
      // Privacy mode: Search by area name
      const areaSearch = getAreaDestination();
      window.open(`https://www.google.com/maps/search/${areaSearch}`, '_blank');
    } else {
      // Exact mode: Use precise coordinates
      window.open(`https://www.google.com/maps?q=${coords.lat},${coords.lng}`, '_blank');
    }
  };

  // Adjust zoom for different modes
  const compactZoom = isAreaMode ? 12 : 13;
  const modalZoom = isAreaMode ? 14 : 15;

  // Render the map content (shared between compact and modal)
  const renderMapContent = (isModal = false) => (
    <MapContainer
      center={[coords.lat, coords.lng]}
      zoom={isModal ? modalZoom : compactZoom}
      style={{ height: '100%', width: '100%', background: '#e2e8f0' }}
      scrollWheelZoom={isModal} // Enable scroll zoom in modal
      zoomControl={isModal} // Show zoom controls in modal
      dragging={isModal} // Enable dragging in modal
      doubleClickZoom={isModal}
      touchZoom={isModal}
    >
      <MapResizer />
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      
      {isAreaMode ? (
        // Area mode: Show circle instead of marker
        <Circle
          center={[coords.lat, coords.lng]}
          radius={500} // 500 meters radius
          pathOptions={{
            color: '#3b82f6',
            fillColor: '#3b82f6',
            fillOpacity: 0.15,
            weight: 2,
            dashArray: '5, 10'
          }}
        />
      ) : (
        // Exact mode: Show marker
        <Marker position={[coords.lat, coords.lng]}>
          <Popup>
            <div className="text-center">
              <strong className="block mb-1">{title}</strong>
              <span className="text-sm text-gray-600">{fullLocationText}</span>
            </div>
          </Popup>
        </Marker>
      )}
    </MapContainer>
  );

  // Compact mode: Clickable thumbnail that opens modal
  if (compact) {
    return (
      <>
        {/* Clickable Map Thumbnail */}
        <div 
          className="h-full cursor-pointer group relative"
          style={{ minHeight: '140px' }}
          onClick={() => setIsModalOpen(true)}
          data-testid="listing-map"
        >
          <div className="h-full rounded-xl overflow-hidden border border-border bg-slate-200 dark:bg-slate-700">
            <div style={{ height: '140px', pointerEvents: 'none' }}>
              {renderMapContent(false)}
            </div>
          </div>
          
          {/* Overlay with expand icon */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors rounded-xl flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white dark:bg-slate-800 rounded-full p-2 shadow-lg">
              <Maximize2 className="w-5 h-5 text-primary" />
            </div>
          </div>
          
          {/* Area mode indicator */}
          {isAreaMode && (
            <div className="absolute bottom-2 left-2 bg-white/90 dark:bg-slate-800/90 px-2 py-1 rounded-lg text-xs font-medium text-blue-600 dark:text-blue-400 shadow-sm">
              📍 Approximate area
            </div>
          )}
          
          {/* Tap to expand hint */}
          <div className="absolute bottom-2 right-2 bg-white/90 dark:bg-slate-800/90 px-2 py-1 rounded-lg text-xs text-muted-foreground shadow-sm flex items-center gap-1">
            <ZoomIn className="w-3 h-3" />
            Tap to expand
          </div>
        </div>

        {/* Address below compact map - clickable for directions */}
        <button
          onClick={openDirections}
          className="mt-2 w-full p-2 bg-secondary/50 hover:bg-secondary rounded-lg transition-colors group/addr"
          data-testid="map-address-directions"
        >
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-primary flex-shrink-0" />
            <span className="text-xs text-foreground text-left flex-1 line-clamp-1">
              {displayLocationText || 'View location'}
            </span>
            <Navigation className="w-3 h-3 text-primary opacity-0 group-hover/addr:opacity-100 transition-opacity" />
          </div>
        </button>

        {/* Fullscreen Modal - Rendered via Portal to escape sidebar constraints */}
        {isModalOpen && createPortal(
          <div 
            className="fixed inset-0 z-[150] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setIsModalOpen(false)}
          >
            <div 
              className="w-full max-w-lg md:max-w-xl lg:max-w-2xl h-[70vh] max-h-[600px] bg-card rounded-2xl overflow-hidden shadow-2xl flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-border bg-card">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-primary" />
                  <h3 className="font-semibold text-foreground">Location</h3>
                  {isAreaMode && (
                    <span className="text-xs bg-blue-500/10 text-blue-600 dark:text-blue-400 px-2 py-0.5 rounded-full">
                      Approximate area
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="p-2 hover:bg-secondary rounded-full transition-colors"
                  data-testid="close-map-modal"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Interactive Map */}
              <div className="flex-1 relative">
                {renderMapContent(true)}
                
                {/* Area mode notice overlay */}
                {isAreaMode && (
                  <div className="absolute top-4 left-4 right-4 md:right-auto md:max-w-sm">
                    <div className="flex items-center gap-2 p-3 bg-blue-500/90 dark:bg-blue-600/90 text-white rounded-xl text-sm shadow-lg">
                      <Shield className="w-4 h-4 flex-shrink-0" />
                      <span>
                        Seller has chosen to show approximate area. Contact them for exact location.
                      </span>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Modal Footer - Address & Actions */}
              <div className="p-4 border-t border-border bg-card space-y-3">
                {/* Address */}
                <div className="flex items-start gap-2 p-3 bg-secondary/50 rounded-xl">
                  <MapPin className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">
                      {isAreaMode ? 'Area' : 'Address'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {displayLocationText || 'Location not specified'}
                    </p>
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={openDirections}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 transition-colors"
                    data-testid="modal-get-directions"
                  >
                    <Navigation className="w-4 h-4" />
                    Get Directions
                  </button>
                  <button
                    onClick={openInGoogleMaps}
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-secondary hover:bg-secondary/80 rounded-xl font-medium transition-colors"
                    data-testid="modal-open-maps"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Open in Maps
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
      </>
    );
  }

  // Full mode (non-compact): Standard map with all controls
  return (
    <div className="space-y-3" data-testid="listing-map">
      {/* Area Mode Notice */}
      {isAreaMode && (
        <div className="flex items-center gap-2 p-3 bg-blue-500/10 border border-blue-500/30 rounded-xl text-sm">
          <Shield className="w-4 h-4 text-blue-500 flex-shrink-0" />
          <span className="text-blue-700 dark:text-blue-300">
            Seller has chosen to show approximate area for privacy. Contact them for exact location.
          </span>
        </div>
      )}

      {/* Map Container */}
      <div className="h-64 md:h-80 rounded-xl overflow-hidden border border-border relative bg-slate-200 dark:bg-slate-700">
        {renderMapContent(true)}
        
        {/* Area mode overlay indicator */}
        {isAreaMode && (
          <div className="absolute bottom-2 left-2 bg-white/90 dark:bg-slate-800/90 px-2 py-1 rounded-lg text-xs font-medium text-blue-600 dark:text-blue-400 shadow-sm">
            📍 Approximate area
          </div>
        )}
      </div>
      
      {/* Location Actions */}
      <div className="flex gap-2">
        <button
          onClick={openDirections}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-white hover:bg-primary/90 rounded-xl text-sm font-medium transition-colors"
          data-testid="get-directions-btn"
        >
          <Navigation className="w-4 h-4" />
          Get Directions
        </button>
      </div>
      
      {/* Address Display */}
      {displayLocationText && (
        <div className="p-3 bg-secondary/50 rounded-xl">
          <div className="flex items-start gap-2">
            <MapPin className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
            <span className="text-sm text-foreground">{displayLocationText}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListingMap;
